import {all, call, fork, put, takeEvery} from 'bukku/shared/SagaService';
import {message} from 'antd';
import actions from './actions';
import AppActions from '../App/actions';
import {deleto, get, patch, post, puto} from 'bukku/shared/ApiService';
import {t} from "@lingui/macro";

// Read currencies
export function* readCurrenciesRequest() {
    yield takeEvery(actions.READ_CURRENCIES_REQUEST, function* ({ payload }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/currencies', payload, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.READ_CURRENCIES_SUCCESS,
                currencies: data.currencies,
            });
        }
        catch (error) {
            yield put({
                type: actions.READ_CURRENCIES_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}
export function* readCurrenciesSuccess() {
    yield takeEvery(actions.READ_CURRENCIES_SUCCESS, () => {});
}
export function* readCurrenciesError() {
    yield takeEvery(actions.READ_CURRENCIES_ERROR, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Activate currency
export function* activateCurrencyRequest() {
    yield takeEvery(actions.ACTIVATE_CURRENCY_REQUEST, function* ({ code }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            yield call(post, '/currencies', code, context);

            // OK
            yield put({
                type: actions.ACTIVATE_CURRENCY_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.ACTIVATE_CURRENCY_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}
export function* activateCurrencySuccess() {
    yield takeEvery(actions.ACTIVATE_CURRENCY_SUCCESS, function* () {
        yield message.success(t`Currency is activated.`);
    });
}
export function* activateCurrencyError() {
    yield takeEvery(actions.ACTIVATE_CURRENCY_ERROR, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Archive currency
export function* archiveCurrencyRequest() {
    yield takeEvery(actions.ARCHIVE_CURRENCY_REQUEST, function* ({ code, payload }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(patch, `/currencies/${code}`, payload, context);

            yield put({
                type: actions.ARCHIVE_CURRENCY_SUCCESS,
                payload,
            });
        }
        catch (error) {
            yield put({
                type: actions.ARCHIVE_CURRENCY_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* archiveCurrencySuccess() {
    yield takeEvery(actions.ARCHIVE_CURRENCY_SUCCESS, function* ({ payload }) {
        yield message.success(
            payload.is_archived ? t`Currency was deactivated.` : t`Currency was activated.`,
        );
    });
}
export function* archiveCurrencyError() {
    yield takeEvery(actions.ARCHIVE_CURRENCY_ERROR, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Read currency rate
export function* readCurrencyRateRequest() {
    yield takeEvery(actions.READ_CURRENCY_RATE_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/v2/lists/currency_rate', request, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.READ_CURRENCY_RATE_SUCCESS,
                currencyRate: data.item.rate,
            });
        }
        catch (error) {
            yield put({
                type: actions.READ_CURRENCY_RATE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}
export function* readCurrencyRateSuccess() {
    yield takeEvery(actions.READ_CURRENCY_RATE_SUCCESS, () => {});
}
export function* readCurrencyRateError() {
    yield takeEvery(actions.READ_CURRENCY_RATE_ERROR, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Read a single currency's exchange rates
export function* readCurrencyRatesRequest() {
    yield takeEvery(actions.READ_CURRENCY_RATES_REQUEST, function* ({ currencyCode, queryStringObject },
        context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, `/currencies/${currencyCode}/rates`, queryStringObject, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.READ_CURRENCY_RATES_SUCCESS,
                currencyRates: data.currency_rates,
                pagination: data.paging,
            });
        }
        catch (error) {
            yield put({
                type: actions.READ_CURRENCY_RATES_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}
export function* readCurrencyRatesSuccess() {
    yield takeEvery(actions.READ_CURRENCY_RATES_SUCCESS, () => {});
}
export function* readCurrencyRatesError() {
    yield takeEvery(actions.READ_CURRENCY_RATES_ERROR, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Create exchange rate for currency
export function* createCurrencyRateRequest() {
    yield takeEvery(actions.CREATE_CURRENCY_RATE_REQUEST, function* ({ currencyCode, request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            const response = yield call(post, `/currencies/${currencyCode}/rates`, request, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.CREATE_CURRENCY_RATE_SUCCESS,
                currencyRate: data.currency_rate,
            });
        }
        catch (error) {
            yield put({
                type: actions.CREATE_CURRENCY_RATE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* createCurrencyRateSuccess() {
    yield takeEvery(actions.CREATE_CURRENCY_RATE_SUCCESS, function* () {
        yield message.success(t`Exchange rate successfully created.`);
    });
}
export function* createCurrencyRateError() {
    yield takeEvery(actions.CREATE_CURRENCY_RATE_ERROR, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Update exchange rate
export function* updateCurrencyRateRequest() {
    yield takeEvery(actions.UPDATE_CURRENCY_RATE_REQUEST, function* ({ currencyCode, id, request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            yield call(puto, `/currencies/${currencyCode}/rates/${id}`, request, context);

            yield put({
                type: actions.UPDATE_CURRENCY_RATE_SUCCESS,
                request,
            });
        }
        catch (error) {
            yield put({
                type: actions.UPDATE_CURRENCY_RATE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* updateCurrencyRateSuccess() {
    yield takeEvery(actions.UPDATE_CURRENCY_RATE_SUCCESS, function* () {
        yield message.success(t`Exchange rate is updated.`);
    });
}
export function* updateCurrencyRateError() {
    yield takeEvery(actions.UPDATE_CURRENCY_RATE_ERROR, function* ({ error }) {
        yield message.error(error.message);
    });
}

// Delete group
export function* deleteCurrencyRateRequest() {
    yield takeEvery(actions.DELETE_CURRENCY_RATE_REQUEST, function* ({ currencyCode, id }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(deleto, `/currencies/${currencyCode}/rates/${id}`, null, context);

            // OK
            yield put({
                type: actions.DELETE_CURRENCY_RATE_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.DELETE_CURRENCY_RATE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}
export function* deleteCurrencyRateSuccess() {
    yield takeEvery(actions.DELETE_CURRENCY_RATE_SUCCESS, function* () {
        yield message.success(t`The currency rate is deleted.`);
    });
}
export function* deleteCurrencyRateError() {
    yield takeEvery(actions.DELETE_CURRENCY_RATE_ERROR, function* ({ error }) {
        yield message.error(error.message);
    });
}

export default function* rootSaga() {
    yield all([
        fork(readCurrenciesRequest),
        fork(readCurrenciesSuccess),
        fork(readCurrenciesError),
        fork(activateCurrencyRequest),
        fork(activateCurrencySuccess),
        fork(activateCurrencyError),
        fork(archiveCurrencyRequest),
        fork(archiveCurrencySuccess),
        fork(archiveCurrencyError),
        fork(readCurrencyRateRequest),
        fork(readCurrencyRateSuccess),
        fork(readCurrencyRatesRequest),
        fork(readCurrencyRatesSuccess),
        fork(readCurrencyRateError),
        fork(createCurrencyRateRequest),
        fork(createCurrencyRateSuccess),
        fork(createCurrencyRateError),
        fork(updateCurrencyRateRequest),
        fork(updateCurrencyRateSuccess),
        fork(updateCurrencyRateError),
        fork(deleteCurrencyRateRequest),
        fork(deleteCurrencyRateSuccess),
        fork(deleteCurrencyRateError),
    ]);
}
